/**
 * @module app/api/apiCall
 */

import { identityHeadersPromise } from '@/providers/IdentityProvider';

/**
 * @func apiCallNoCatch
 * @summary Same as apiCall, except it does not catch errors
 * A convenience function for making API calls.
 * @param  {string}   endpoint used in `/.netlify/functions/${endpoint}`
 * @param  {object}   options  fetch parameters
 * @param  {object}   data     if truthy, the options object's "body" field will
 *                             be overwritten with `body: JSON.stringify(data)`
 *
 * @return {object}            the return value from the server
 */
export const apiCallNoCatch = (endpoint, data = {}, options = {}) =>
  identityHeadersPromise().then((headers) =>
    // eslint-disable-next-line implicit-arrow-linebreak
    window
      .fetch(`/.netlify/functions/${endpoint}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
          // 'x-webhook-secret': process.env.ASSET_WEBHOOK_SECRET,
        },
        ...options,
        ...(Object.keys(data).length > 0 && { body: JSON.stringify(data) }),
      })
      .then(async (res) => {
        const { status } = res;
        const resData = await res.json();
        // eslint-disable-next-line no-console
        window.debug &&
          console.log(
            `%cstatus [${status}] response from ${endpoint} , data:`,
            'background:cadetblue; color:white;',
            resData,
          );
        return [resData, status];
      }),
  );

/**
 * @func apiCall
 * @summary A convenience function for making API calls.
 * @param  {string}   endpoint used in `/.netlify/functions/${endpoint}`
 * @param  {object}   options  fetch parameters
 * @param  {object}   data     if truthy, the options object's "body" field will
 *                             be overwritten with `body: JSON.stringify(data)`
 *
 * @return {object}            the return value from the server
 */
export const apiCall = (endpoint, data = {}, option = {}) =>
  apiCallNoCatch(endpoint, data, option).catch((err) => console.error(err));

/**
 * @func apiCallAsync
 * @summary An async/await wrapper around apiCallNoCatch for modern async patterns.
 * @param  {string}   endpoint used in `/.netlify/functions/${endpoint}`
 * @param  {object}   data     if truthy, the options object's "body" field will
 *                             be overwritten with `body: JSON.stringify(data)`
 * @param  {object}   options  fetch parameters
 *
 * @return {Promise<object>}   the return value from the server
 */
export const apiCallAsync = async (endpoint, data = {}, options = {}) => {
  try {
    return await apiCallNoCatch(endpoint, data, options);
  } catch (err) {
    console.error(err);
    return null;
  }
};
